import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  Button,
  Input,
  Label,
  Textarea,
} from 'lift-ui';
import '../../iam-support.styles.scss';

interface AddProductSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (name: string, key?: string, description?: string) => void;
  isSubmitting?: boolean;
}

const AddProductSheet: React.FC<AddProductSheetProps> = ({
  open,
  onOpenChange,
  onAdd,
  isSubmitting = false,
}) => {
  const [productName, setProductName] = useState('');
  const [productKey, setProductKey] = useState('');
  const [description, setDescription] = useState('');

  const generateKey = (name: string): string => {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  const handleNameChange = (value: string) => {
    setProductName(value);
    // Auto-generate key if user hasn't manually edited it
    if (!productKey || productKey === generateKey(productName)) {
      setProductKey(generateKey(value));
    }
  };

  const handleAdd = () => {
    if (productName.trim()) {
      const key = productKey.trim() || generateKey(productName);
      onAdd(productName.trim(), key, description.trim() || undefined);
      resetForm();
      onOpenChange(false);
    }
  };

  const resetForm = () => {
    setProductName('');
    setProductKey('');
    setDescription('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="md" className="iam-support-sheet flex flex-col h-full">
        <SheetHeader>
          <SheetTitle>Add New Product</SheetTitle>
          <SheetDescription>
            Add a new product or functionality to the IAM system. 
            Users can then be assigned permissions for this product.
          </SheetDescription>
        </SheetHeader>
        <div className="iam-support-dialog-form flex-1 overflow-y-auto">
          {/* Info Banner */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                <strong>Note:</strong> Adding products may require backend configuration. 
                Contact your administrator if the product doesn't appear after creation.
              </p>
            </div>
          </div>

          <div className="iam-support-form-field">
            <Label htmlFor="new-product-name">Product Name *</Label>
            <Input 
              id="new-product-name" 
              placeholder="e.g., Report Builder, Data Analytics"
              value={productName}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </div>

          <div className="iam-support-form-field">
            <Label htmlFor="new-product-key">Product Key</Label>
            <Input 
              id="new-product-key" 
              placeholder="e.g., report_builder"
              value={productKey}
              onChange={(e) => setProductKey(e.target.value)}
              className="font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Unique identifier used in the system. Auto-generated from name if left empty.
            </p>
          </div>

          <div className="iam-support-form-field">
            <Label htmlFor="new-product-description">Description</Label>
            <Textarea 
              id="new-product-description" 
              placeholder="Optional: Describe what this product/feature does"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <SheetFooter className="border-t pt-4 mt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleAdd}
            disabled={!productName.trim() || isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Product'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AddProductSheet;
